import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import { useSecurityLogger } from "@/hooks/useSecurityLogger";
import { supabase } from "@/integrations/supabase/client";
import { ContentSkeleton } from "@/components/dashboard/premium/ContentSkeleton";

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * Lightweight admin gate. Renders INSIDE the persistent AppShellLayout, so
 * navigating to/from admin pages does NOT remount the sidebar/topbar. While
 * the role resolves we show only a content-area skeleton (no full-page spinner).
 */
export function AdminRoute({ children }: AdminRouteProps) {
  const { isAdmin, isLoading } = useUserRole();
  const navigate = useNavigate();
  const [shouldRender, setShouldRender] = useState(true);
  const redirectTimeout = useRef<NodeJS.Timeout>();
  const { logSecurityEvent } = useSecurityLogger();
  const hasLoggedRef = useRef(false);

  useEffect(() => {
    const logUnauthorizedAttempt = async () => {
      if (!isLoading && !isAdmin && !hasLoggedRef.current) {
        hasLoggedRef.current = true;
        const { data: { user } } = await supabase.auth.getUser();

        await logSecurityEvent({
          event_type: "admin_access_attempt",
          description: `Unauthorized admin area access attempt by ${user?.email || "unauthenticated user"}`,
          severity: "critical",
          user_id: user?.id,
          was_blocked: true,
          details: {
            attempted_route: window.location.pathname,
            user_email: user?.email,
            timestamp: new Date().toISOString(),
          },
        });
      }
    };

    if (!isLoading && !isAdmin) {
      logUnauthorizedAttempt();
      redirectTimeout.current = setTimeout(() => {
        setShouldRender(false);
        navigate("/dashboard");
      }, 300);
    } else {
      setShouldRender(true);
      if (redirectTimeout.current) {
        clearTimeout(redirectTimeout.current);
      }
    }

    return () => {
      if (redirectTimeout.current) {
        clearTimeout(redirectTimeout.current);
      }
    };
  }, [isAdmin, isLoading, navigate, logSecurityEvent]);

  if (isLoading) {
    return <ContentSkeleton />;
  }

  if (!isAdmin || !shouldRender) {
    return null;
  }

  return <>{children}</>;
}
